import { motion, AnimatePresence } from "framer-motion";
import { 
  PenTool, 
  Tag, 
  ShieldCheck, 
  RefreshCw, 
  Send,
  Clock,
  CheckCircle2,
  Zap,
  Feather
} from "lucide-react";
import { useState, useEffect } from "react";

// Brand colors
const PRIMARY_BLUE = "#1356a6";
const ACCENT_COLOR = "#0AB89E";

const heroImage = "/assets/cards/abstract_digital_clinical_documentation_background.png";
const appScreen = "/assets/cards/clinical_note_review_interface_with_smart_codes.png";

export default function ClinicalDocumentationCard() {
  const features = [
    {
      icon: PenTool,
      title: "One-Click Attestation",
      desc: "Review, tweak, sign—AI drafts, you finalize in moments"
    },
    {
      icon: Tag,
      title: "Smart Code Suggestions",
      desc: "ICD-10 and CPT codes recommended based on encounter content"
    },
    {
      icon: ShieldCheck,
      title: "Compliance Baked In",
      desc: "Payer-specific documentation requirements checked before submission"
    },
    {
      icon: RefreshCw,
      title: "Instant Notes",
      desc: "Encounter, Referrals, Billing, Discharge and Lab/Image Reports"
    },
    {
      icon: Send,
      title: "Patient-Friendly Summaries",
      desc: "Auto-generated visit notes delivered via email, portal, or text"
    }
  ];

  const kpis = [
    { label: "Documentation Time", value: "-65%", icon: Clock, color: "text-emerald-400" },
    { label: "Coding Accuracy", value: "94%", icon: CheckCircle2, color: "text-blue-400" },
    { label: "Compliance Rate", value: "99%", icon: ShieldCheck, color: "text-purple-400" },
    { label: "Note Completion", value: "< 2 min", icon: Zap, color: "text-amber-400" },
  ];

  const [currentKpi, setCurrentKpi] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentKpi((prev) => (prev + 1) % kpis.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [kpis.length]);

  return (
    <div className="w-full h-full">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="relative bg-white/80 backdrop-blur-xl border border-white/20 rounded-2xl overflow-hidden shadow-2xl grid lg:grid-cols-2 gap-0 h-[700px]"
      >
        {/* Left Column: Content */}
        <div className="p-6 md:p-8 flex flex-col justify-start relative z-10 overflow-hidden">
          <div className="mb-3 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-sm font-bold uppercase tracking-wider w-fit" style={{ color: PRIMARY_BLUE }}>
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: PRIMARY_BLUE }}></span>
              <span className="relative inline-flex rounded-full h-3 w-3" style={{ backgroundColor: PRIMARY_BLUE }}></span>
            </span>
            Clinical Documentation
          </div>
          
          <h1 className="text-3xl md:text-4xl font-normal mb-2 tracking-tight" style={{ color: PRIMARY_BLUE }}>
            Write Less. Document Everything.
          </h1>
          
          <h2 className="text-lg md:text-xl font-medium mb-6" style={{ color: `${PRIMARY_BLUE}cc` }}>
            AI-generated clinical notes that capture the full story.
          </h2>
          
          <p className="text-lg text-slate-500 mb-8 italic border-l-4 pl-5" style={{ borderColor: `${ACCENT_COLOR}80` }}>
            "Compliant, complete, in seconds. Review, tweak, and sign."
          </p>

          <div className="space-y-3">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 + (index * 0.1), duration: 0.5 }}
                className="flex gap-5 group"
              >
                <div className="flex-shrink-0">
                  <div 
                    className="w-12 h-12 rounded-xl bg-slate-50 flex items-center justify-center transition-colors duration-300 shadow-sm group-hover:text-white"
                    style={{ 
                      '--hover-bg': PRIMARY_BLUE 
                    } as React.CSSProperties}
                  >
                    <feature.icon className="w-6 h-6 transition-colors" style={{ color: ACCENT_COLOR }} />
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-lg transition-colors" style={{ color: PRIMARY_BLUE }}>
                    {feature.title}
                  </h3>
                  <p className="text-base text-slate-500 leading-relaxed">
                    {feature.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Right Column: Visuals */}
        <div className="relative h-full overflow-hidden bg-slate-900 flex items-center justify-center">
          
          {/* Background Layer */}
          <div className="absolute inset-0 z-0">
             <img 
              src={heroImage} 
              alt="Background Flow" 
              className="w-full h-full object-cover opacity-80"
            />
            <div className="absolute inset-0 bg-slate-900/30 mix-blend-multiply" />
          </div>

          {/* Floating Interface Screen */}
          <motion.div 
            initial={{ opacity: 0, y: 40, rotateX: 10 }}
            animate={{ opacity: 1, y: 0, rotateX: 0 }}
            transition={{ delay: 0.4, duration: 0.8, ease: "easeOut" }}
            className="relative z-10 w-[90%] max-w-lg aspect-[4/3] bg-white rounded-xl shadow-2xl overflow-hidden border border-white/20 -mt-8"
          >
             {/* Window Controls */}
             <div className="absolute top-0 left-0 right-0 h-8 bg-slate-100 border-b border-slate-200 flex items-center px-4 gap-2 z-20">
               <div className="w-3 h-3 rounded-full bg-red-400" />
               <div className="w-3 h-3 rounded-full bg-amber-400" />
               <div className="w-3 h-3 rounded-full bg-green-400" />
             </div>

             {/* Screen Content */}
             <div className="mt-8 h-full w-full">
               <img 
                src={appScreen} 
                alt="Clinical Documentation Interface" 
                className="w-full h-full object-cover object-top"
              />
             </div>
             
             {/* Screen Glare */}
             <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent pointer-events-none z-20" />
          </motion.div>

          {/* Floating Feature Context Cards */}
          <div className="absolute inset-0 z-20 pointer-events-none">
             
             {/* Smart Code Suggestion - Top Right */}
             <motion.div
               animate={{ y: [0, -10, 0], opacity: [0.85, 1, 0.85] }}
               transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 }}
               className="absolute top-[12%] right-[8%] bg-white/95 backdrop-blur text-slate-800 p-4 rounded-xl shadow-xl flex items-center gap-3 max-w-[210px]"
             >
               <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 shrink-0">
                 <Tag className="w-5 h-5" />
               </div>
               <div>
                 <div className="text-xs font-bold text-slate-500 uppercase">Suggested Code</div>
                 <div className="text-base font-bold text-slate-800">E11.9 • Type 2 DM</div>
               </div>
             </motion.div>

             {/* Compliance Check - Bottom Left */}
             <motion.div
               animate={{ y: [0, 10, 0], opacity: [0.85, 1, 0.85] }}
               transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
               className="absolute bottom-[35%] left-[4%] bg-emerald-50/95 backdrop-blur border border-emerald-200 text-slate-800 p-4 rounded-xl shadow-xl flex items-center gap-3 max-w-[200px]"
             >
               <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 shrink-0">
                 <ShieldCheck className="w-5 h-5" />
               </div>
               <div>
                 <div className="text-xs font-bold text-emerald-600 uppercase">Compliance</div>
                 <div className="text-base font-bold text-slate-800">100% Verified</div>
               </div>
             </motion.div>

             {/* One-Click Attest - Middle Left */}
             <motion.div
               animate={{ scale: [1, 1.05, 1], opacity: [0.9, 1, 0.9] }}
               transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 2 }}
               className="absolute top-[38%] left-[12%] backdrop-blur text-white p-3 px-6 rounded-full shadow-xl flex items-center gap-3"
               style={{ backgroundColor: `${PRIMARY_BLUE}e6` }}
             >
               <Feather className="w-4 h-4" />
               <span className="text-base font-bold">Signed</span>
             </motion.div>

          </div>

          {/* Floating UI Cards (Context) - KPI Carousel */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30 w-72 hidden md:block">
            <div className="relative h-20 perspective-1000">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentKpi}
                  initial={{ opacity: 0, y: 15, rotateX: 10 }}
                  animate={{ opacity: 1, y: 0, rotateX: 0 }}
                  exit={{ opacity: 0, y: -15, rotateX: -10 }}
                  transition={{ duration: 0.5, ease: "backOut" }}
                  className="absolute inset-0 backdrop-blur-md border border-white/20 px-4 py-3 rounded-xl flex items-center gap-4 overflow-hidden"
                  style={{
                    backgroundColor: `${PRIMARY_BLUE}e6`,
                    boxShadow: `0 10px 30px -10px ${PRIMARY_BLUE}99`
                  }}
                >
                   {/* Glare Effect */}
                   <div className="absolute inset-0 bg-gradient-to-tr from-white/10 to-transparent pointer-events-none" />

                  <div className="p-3 bg-white/10 rounded-lg backdrop-blur-sm border border-white/10 relative z-10">
                    {(() => {
                      const Icon = kpis[currentKpi].icon;
                      return <Icon className="w-6 h-6 text-white" />;
                    })()}
                  </div>
                  <div className="relative z-10 flex-1">
                    <div className="text-xs font-bold text-white/70 uppercase tracking-wider leading-tight">
                      {kpis[currentKpi].label}
                    </div>
                    <div className="text-2xl font-bold text-white drop-shadow-sm">
                      {kpis[currentKpi].value}
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
            
            {/* Carousel Indicators */}
            <div className="flex justify-center gap-1.5 mt-2">
              {kpis.map((_, idx) => (
                <div 
                  key={idx} 
                  className={`h-1 rounded-full transition-all duration-300 shadow-sm ${
                    idx === currentKpi ? "w-4" : "w-1.5 bg-slate-300/50"
                  }`}
                  style={{
                    backgroundColor: idx === currentKpi ? PRIMARY_BLUE : undefined
                  }}
                />
              ))}
            </div>
          </div>

        </div>
      </motion.div>
    </div>
  );
}

